import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Avatar, Badge, Box, Button, Card, Flex, Heading, Text } from '@radix-ui/themes';
import { useWallets } from '@rango-dev/widget-embedded';
import { Wallet } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { emitProfileEvent, ProfileEventTypes } from '../../services/eventEmitter';
import { createSignMessage } from '../../store/thunks';
import { getConciseAddress } from '../../utils/mod';
import { supportedBlockchainsDisplayName } from '../ProfileWalletsModal/ProfileWalletsModal.constants';
const ADDRESS_MAX_CHARS = 8;
const WALLET_ICON_SIZE = 16;
const cardStyles = {
    '--base-card-padding-top': '14px',
    '--base-card-padding-bottom': '14px',
    '--base-card-padding-left': 'var(--space-4)',
    '--base-card-padding-right': 'var(--space-4)',
    '--base-card-border-radius': 'var(--radius-3)',
};
const headingStyles = { '--line-height': 'var(--line-height-2)' };
const signMessage = createSignMessage();
export function ProfileWallet(props) {
    const { account: { address, blockchain, walletType }, data: { isUserLoggedIn, selectedAccount }, onLogin, onMerge, onError, } = props;
    const dispatch = useDispatch();
    const { getWalletInfo, getSigners } = useWallets();
    const signMessagePromise = useRef(null);
    let image = null;
    let name = null;
    const { t } = useTranslation();
    if (walletType) {
        const walletInfo = getWalletInfo(walletType);
        image = walletInfo.img;
        name = walletInfo.name;
    }
    const conciseAddress = getConciseAddress(address, ADDRESS_MAX_CHARS);
    const blockchainDisplayName = supportedBlockchainsDisplayName[blockchain] ?? blockchain;
    const loading = selectedAccount?.address === address &&
        selectedAccount?.blockchain === blockchain &&
        selectedAccount?.walletType === walletType;
    const disabled = !!selectedAccount;
    const actionButtonId = `profile-page-wallet-${isUserLoggedIn ? 'add-to-profile' : 'login'}-btn`;
    const walletName = walletType ?? '';
    const signMessageCallback = (profileSummary, meta) => {
        emitProfileEvent({
            type: meta.actionType === 'login'
                ? ProfileEventTypes.LOGIN_COMPLETED
                : ProfileEventTypes.MERGE_COMPLETED,
            payload: { walletName },
        });
        if (meta.actionType === 'login') {
            onLogin?.(profileSummary);
        }
        else {
            onMerge?.(profileSummary);
        }
    };
    const handleError = (error) => {
        emitProfileEvent({
            type: isUserLoggedIn ? ProfileEventTypes.MERGE_FAILED : ProfileEventTypes.LOGIN_FAILED,
            payload: { walletName },
        });
        onError?.(error);
    };
    useEffect(() => {
        return () => signMessagePromise.current?.abort();
    }, []);
    const handleSignMessage = () => {
        if (walletType) {
            emitProfileEvent({
                type: isUserLoggedIn
                    ? ProfileEventTypes.MERGE_INITIATED
                    : ProfileEventTypes.LOGIN_INITIATED,
                payload: { walletName },
            });
            signMessagePromise.current = dispatch(signMessage({
                account: { address, blockchain, walletType },
                getSigners,
                callback: signMessageCallback,
                errorCallback: handleError,
            }));
        }
    };
    return (_jsx(Card, { size: "2", variant: "surface", style: cardStyles, children: _jsxs(Flex, { align: "center", gap: "2", justify: "between", children: [_jsxs(Flex, { align: "center", gap: "2", flexGrow: "1", minWidth: "0", children: [_jsx(Avatar, { size: "2", radius: "full", src: image ?? undefined, fallback: _jsx(Wallet, { size: WALLET_ICON_SIZE }), alt: name ?? blockchainDisplayName }), _jsxs(Flex, { direction: "column", align: "start", justify: "center", gap: "1", minWidth: "0", children: [_jsxs(Flex, { align: "center", gap: "1", minWidth: "0", children: [name && (_jsx(Heading, { as: "h3", size: "2", weight: "medium", truncate: true, style: headingStyles, children: name })), _jsx(Badge, { color: "sky", variant: "soft", size: "1", children: blockchainDisplayName })] }), _jsx(Text, { size: "1", color: "gray", truncate: true, children: conciseAddress ?? address })] })] }), _jsx(Box, { flexShrink: "0", asChild: true, children: _jsx(Button, { id: actionButtonId, size: "2", radius: "full", disabled: disabled, loading: loading, onClick: handleSignMessage, children: isUserLoggedIn ? t('Add To Profile') : t('Login') }) })] }) }));
}
